/**
 * Экран режима «Свой текст».
 *
 * Сначала пользователь приносит материал, потом печатает упражнение из него.
 * Экран прямо говорит, что текст остаётся на устройстве: обещание приватности
 * стоит ровно столько, сколько понятно пользователю (ТЗ §11.2). Обещание
 * стоит там, где текст приносят, — в форме; на экране набора оно раскрывается
 * под упражнением (ТЗ FR-EN-84).
 */

import { useState } from 'react';
import type { SessionId } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { Button, Card } from '@type2x/ui';
import { MIN_WORDS, type CustomTextPlan } from '@type2x/feature-custom-text';

import type { Runtime } from '../runtime.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface CustomTextScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  /** Занятие; null — текст ещё не принесён. */
  readonly plan: CustomTextPlan | null;
  readonly sessionId: SessionId;
  /** Разбирает принесённый текст и, если материала хватает, строит занятие. */
  readonly onTextSubmit: (text: string) => void;
  /** Текст принесён, но материала не хватило. */
  readonly tooShort: boolean;
  readonly onFinished: (run: TypingSession) => void;
}

export function CustomTextScreen({
  runtime,
  t,
  plan,
  sessionId,
  onTextSubmit,
  tooShort,
  onFinished,
}: CustomTextScreenProps): React.JSX.Element {
  if (plan === null) {
    return <TextForm t={t} onSubmit={onTextSubmit} tooShort={tooShort} />;
  }

  return (
    <DrillLayout
      t={t}
      title={t('customText.title')}
      now={<span className="t2x-muted">{t('customText.words', { count: plan.units.length })}</span>}
      details={
        <>
          <p className="t2x-muted">{t('customText.local')}</p>

          {plan.rejectedWords > 0 && (
            <p className="t2x-muted">
              {t('customText.rejected', {
                count: plan.rejectedWords,
                characters: plan.unsupportedCharacters.join(' '),
              })}
            </p>
          )}

          <p className="t2x-muted">{t('customText.hint')}</p>
        </>
      }
    >
      <TypingScreen
        key={sessionId}
        runtime={runtime}
        t={t}
        title={t('customText.typing')}
        hint={t('customText.hint')}
        sessionId={sessionId}
        layoutId={plan.layoutId}
        targetText={plan.exercise.text}
        hintLevel={plan.hintLevel}
        compact
        onFinished={onFinished}
      />
    </DrillLayout>
  );
}

/** Форма, в которую пользователь вставляет свой текст. */
function TextForm({
  t,
  onSubmit,
  tooShort,
}: {
  readonly t: Translate;
  readonly onSubmit: (text: string) => void;
  readonly tooShort: boolean;
}): React.JSX.Element {
  const [text, setText] = useState('');

  return (
    <Card title={t('customText.title')}>
      <p className="t2x-muted">{t('customText.local')}</p>

      <label className="t2x-field t2x-field--wide">
        <span>{t('customText.label')}</span>
        <textarea
          rows={8}
          value={text}
          // Автозаполнение и проверка орфографии отправляют текст наружу
          // средствами браузера: режим обещает обратное.
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setText(event.target.value)}
        />
      </label>

      {tooShort && (
        <p role="alert" className="t2x-error">
          {t('customText.tooShort', { count: MIN_WORDS })}
        </p>
      )}

      <Button onClick={() => onSubmit(text)}>{t('customText.begin')}</Button>
    </Card>
  );
}
